import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, Slide, Snackbar, Alert, LinearProgress } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import axios from 'axios';
import { URL as API_BASE } from './BaseURL';

// A single global network-connectivity indicator, mounted at the top of the
// route tree. Users kept typing into forms while their connection was down
// and losing work on submit; this component makes the disconnect impossible
// to miss.
//
// Detection strategy — three signals fold into one state:
//   1. window `online` / `offline` events for instant NIC-level changes.
//   2. Real request failures from any axios call are caught by a global
//      response interceptor and interpreted as "offline" when the error has
//      no HTTP response (i.e. network / DNS / CORS-preflight failure).
//   3. When we believe we're offline, a lightweight GET /health poll runs
//      every POLL_INTERVAL_MS to detect recovery. It stops the moment we're
//      back online, so the steady-state has zero background traffic.
//
// The banner is non-blocking on purpose — MUI dialogs would trap focus and
// break in-flight edits — but its size, color, and pulse are chosen to be
// impossible to overlook.

const HEALTH_URL        = `${API_BASE}health`;
const POLL_INTERVAL_MS  = 8000;   // only while offline
const HEALTH_TIMEOUT_MS = 5000;

export default function NetworkStatusBanner() {
  // Seed from the browser's own belief. `navigator.onLine === false` is
  // authoritative when true, but a `true` value only means "some network
  // exists" — not that our API is reachable. The interceptor + poll refine
  // it from there.
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  // Distinguish the transient "just came back" state so we can show a
  // brief confirmation toast before hiding the banner entirely.
  const [showRestored, setShowRestored] = useState(false);

  const pollTimerRef = useRef(null);
  const wasOfflineRef = useRef(false);

  const markOnline = useCallback(() => {
    setOnline((prev) => {
      if (!prev) {
        // Coming back from offline — flash the restored toast.
        setShowRestored(true);
      }
      return true;
    });
  }, []);

  const markOffline = useCallback(() => {
    setOnline(false);
  }, []);

  // ── Poll /health while offline; stop the moment we recover ──────────────
  const runHealthCheck = useCallback(async () => {
    try {
      await axios.get(HEALTH_URL, { timeout: HEALTH_TIMEOUT_MS });
      markOnline();
    } catch (_) {
      // Still offline — no state change needed, poll will fire again.
    }
  }, [markOnline]);

  useEffect(() => {
    if (!online) {
      wasOfflineRef.current = true;
      // Immediate first check, then interval.
      runHealthCheck();
      pollTimerRef.current = setInterval(runHealthCheck, POLL_INTERVAL_MS);
      return () => clearInterval(pollTimerRef.current);
    }
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    return undefined;
  }, [online, runHealthCheck]);

  // ── Browser online/offline events ───────────────────────────────────────
  useEffect(() => {
    const onOnline  = () => markOnline();
    const onOffline = () => markOffline();
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [markOnline, markOffline]);

  // ── Global axios interceptor — any real request failure with no HTTP
  //    response is treated as "we can't reach the server". Request that
  //    succeed re-mark us as online, which handles the case where the
  //    browser's `online` event fired before the server was actually
  //    reachable (common on wifi handoff / VPN reconnect).
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        markOnline();
        return response;
      },
      (error) => {
        // `error.response` is undefined for network-layer failures
        // (DNS, timeout, offline, CORS-preflight blocked). HTTP 4xx/5xx
        // responses DO get a response object — those don't mean offline,
        // just that the server rejected the request.
        const isNetworkError =
          !error.response &&
          (error.code === 'ERR_NETWORK'
            || error.code === 'ECONNABORTED'
            || error.message === 'Network Error'
            || (typeof navigator !== 'undefined' && navigator.onLine === false));
        if (isNetworkError) markOffline();
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [markOnline, markOffline]);

  return (
    <>
      {/* Persistent bottom banner while offline. Non-blocking, but the
          pulsing red bar + full-width copy is chosen to be unmissable. */}
      <Slide direction="up" in={!online} mountOnEnter unmountOnExit>
        <Box
          role="alert"
          aria-live="assertive"
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2400,          // above MUI modal defaults
            bgcolor: '#b91c1c',    // red-700 — high contrast
            color: '#fff',
            boxShadow: '0 -6px 24px rgba(185,28,28,0.35)',
            borderTop: '3px solid #7f1d1d',
            animation: 'tc-net-pulse 2.2s ease-in-out infinite',
            '@keyframes tc-net-pulse': {
              '0%,100%': { boxShadow: '0 -6px 24px rgba(185,28,28,0.35)' },
              '50%':     { boxShadow: '0 -6px 34px rgba(185,28,28,0.75)' },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              maxWidth: 1400,
              mx: 'auto',
              px: { xs: 2, md: 3 },
              py: 1.5,
            }}
          >
            <CloudOffIcon sx={{ fontSize: 32, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>
                No internet connection
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', opacity: 0.95, mt: 0.25 }}>
                You can keep viewing, but anything you save or submit now will
                fail. Restore your connection before continuing.
              </Typography>
            </Box>
            <Box sx={{ width: 160, display: { xs: 'none', md: 'block' } }}>
              <Typography sx={{ fontSize: '0.72rem', opacity: 0.9, mb: 0.5 }}>
                Reconnecting…
              </Typography>
              <LinearProgress
                color="inherit"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.25)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#fff' },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Slide>

      {/* Brief confirmation when the connection recovers. */}
      <Snackbar
        open={showRestored}
        autoHideDuration={3000}
        onClose={() => setShowRestored(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          icon={<CloudDoneIcon fontSize="inherit" />}
          severity="success"
          variant="filled"
          onClose={() => setShowRestored(false)}
          sx={{ fontWeight: 600 }}
        >
          Connection restored.
        </Alert>
      </Snackbar>
    </>
  );
}
